import React from 'react';
import { motion } from 'framer-motion';
import { GlobeIcon, LinkIcon, CodeIcon, StoreIcon } from 'lucide-react';
import { SlideFrame } from '../SlideFrame';
import { DeckLogoPlate } from '../DeckLogo';
import { GTM_STEPS } from '../../../data/deck';
import { useSequence } from '../../../hooks/useSequence';
const EASE = [0.23, 1, 0.32, 1] as const;
const STOREFRONTS = [{
  name: 'EasyLease Rentals',
  kind: 'Car rental',
  icon: StoreIcon,
  y: 0
}, {
  name: 'NOIR Apparel',
  kind: 'Fashion retail',
  icon: StoreIcon,
  y: 94
}, {
  name: 'MarketVault',
  kind: 'Electronics',
  icon: StoreIcon,
  y: 188
}, {
  name: 'Prime Realty',
  kind: 'Property',
  icon: StoreIcon,
  y: 282
}];
const CHANNELS = [{
  label: 'Website widget',
  icon: CodeIcon
}, {
  label: 'Private link',
  icon: LinkIcon
}, {
  label: 'Embedded',
  icon: GlobeIcon
}];
const NODE_X = 500;
const CENTER_Y = 184;
function StorefrontCard({
  name,
  kind,
  y,
  active,
  delay,
  published
}: {
  name: string;
  kind: string;
  y: number;
  active: boolean;
  delay: number;
  published: boolean;
}) {
  return <motion.div className="absolute left-0 w-[156px] rounded-xl bg-white border border-slate-200 overflow-hidden" style={{
    top: y,
    boxShadow: '0 12px 30px -16px rgba(11,18,32,0.28)'
  }} initial={{
    opacity: 0,
    x: -14
  }} animate={active ? {
    opacity: 1,
    x: 0
  } : {
    opacity: 0,
    x: -14
  }} transition={{
    duration: 0.45,
    ease: EASE,
    delay
  }} data-id="element-1993" data-mp="3r4vtbv9tr1xc">
      <div className="flex items-center gap-1.5 h-6 px-2 bg-slate-50 border-b border-slate-100" data-id="element-1994" data-mp="dru5kavvv6tt">
        <span className="w-3.5 h-3.5 rounded bg-gradient-to-br from-primary-indigo to-ai-purple shrink-0" data-id="element-1995" data-mp="9ra8t8f82587" />
        <span className="text-[7.5px] font-semibold text-dark-navy truncate" data-id="element-1996" data-mp="4y7c4rf5efns">{name}</span>
      </div>
      <div className="p-2 space-y-1.5" data-id="element-1997" data-mp="p18vhklqd5">
        <div className="flex gap-1.5" data-id="element-1998" data-mp="xg2xcjtilass">
          <span className="w-3 h-3 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple shrink-0" data-id="element-1999" data-mp="xh4ixi85pfor" />
          <div className="flex-1 space-y-1" data-id="element-2000" data-mp="so1qjdf48ca5">
            <div className="h-1 w-full rounded-full bg-slate-200" data-id="element-2001" data-mp="1bm4wphe1a4fr" />
            <div className="h-1 w-2/3 rounded-full bg-slate-100" data-id="element-2002" data-mp="1bny3cwonconr" />
          </div>
        </div>
        <div className="h-4 rounded-full bg-slate-50 border border-slate-100" data-id="element-2003" data-mp="1gk6gy5n749st" />
        <div className="flex items-center justify-between" data-id="element-2004" data-mp="12t1llfqv23oa">
          <span className="text-[7px] text-slate-400" data-id="element-2005" data-mp="16umt0lkmlpr3">{kind}</span>
          <motion.span className="px-1.5 py-[2px] rounded-full bg-success/10 text-[6.5px] font-semibold text-success" animate={{
          opacity: published ? 1 : 0
        }} transition={{
          duration: 0.3
        }} data-id="element-2006" data-mp="16ss7b51j5tly">
            Inventory public
          </motion.span>
        </div>
      </div>
    </motion.div>;
}
export function SlideGoToMarket({
  active
}: {
  active: boolean;
}) {
  // 1: connections form · 2: marketplace node appears · 3: inventory goes public
  const step = useSequence([1700, 800, 700], active);
  return <SlideFrame eyebrow="Go-to-market" title="We start with storefronts. Then we unlock the marketplace." footnote={<div className="rounded-2xl bg-dark-navy px-6 py-4" data-id="element-2008" data-mp="6kvk21alu7td">
          <p className="font-display font-bold text-[16px] leading-snug text-white" data-id="element-2009" data-mp="2me9f7rz6f77">
            We do not build the marketplace first.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric-cyan to-ai-purple" data-id="element-2010" data-mp="geuf6gc9grbt">
              We build AI storefronts first — and together, they become the marketplace.
            </span>
          </p>
        </div>} data-id="element-2007" data-mp="2i9a45691hy94">
      <div className="h-full flex gap-8" data-id="element-2011" data-mp="lat2e0lus3an">
        <div className="w-[276px] shrink-0" data-id="element-2012" data-mp="ldyttmhyfh9j">
          <p className="text-[12.5px] leading-relaxed text-slate-500" data-id="element-2013" data-mp="zbvq0hshxlno">
            Talab does not need marketplace supply on day one. Every business can start with its
            own AI storefront immediately.
          </p>
          <ol className="relative mt-5 pl-5" data-id="element-2014" data-mp="313ijcpzv1snb">
            <span className="absolute left-[5px] top-2 bottom-2 w-px bg-slate-200" aria-hidden="true" data-id="element-2015" data-mp="2w45lzbbd536h" />
            {GTM_STEPS.map((gtmStep, index) => <motion.li key={gtmStep.title} className="relative pb-4 last:pb-0" initial={{
            opacity: 0,
            x: -10
          }} animate={active ? {
            opacity: 1,
            x: 0
          } : {
            opacity: 0,
            x: -10
          }} transition={{
            duration: 0.4,
            ease: EASE,
            delay: 0.25 + index * 0.1
          }} data-id="element-2016" data-mp="32k5rmefbtsjt" data-mpi={index}>
                <span className={`absolute -left-5 top-1 w-[11px] h-[11px] rounded-full border-2 border-white ${index < 2 ? 'bg-primary-indigo' : 'bg-ai-purple'}`} data-id="element-2017" data-mp="2evwahpt25sz0" />
                <p className={`text-[8.5px] font-semibold uppercase tracking-[0.18em] ${index < 2 ? 'text-primary-indigo' : 'text-ai-purple'}`} data-id="element-2018" data-mp="2oqxyujfier0j">
                  {gtmStep.phase}
                </p>
                <p className="text-[13px] font-semibold text-dark-navy leading-tight mt-0.5" data-id="element-2019" data-mp="211lqfls0amza">
                  {gtmStep.title}
                </p>
                <p className="text-[11px] text-slate-500 leading-snug mt-1" data-id="element-2020" data-mp="1okubmc4oyr6z">{gtmStep.body}</p>
              </motion.li>)}
          </ol>
        </div>

        <div className="relative flex-1 min-w-0" data-id="element-2021" data-mp="1jnkmyyp4mjk7">
          <div className="absolute inset-0" data-id="element-2022" data-mp="1en6drdbsh3kl">
            {/* Connection layer */}
            <svg className="absolute inset-0 w-full h-full" fill="none" data-id="element-2023" data-mp="1akjit3jtn2j6">
              {STOREFRONTS.map((store, index) => {
              const fromY = store.y + 42;
              return <g key={store.name} data-id="element-2024" data-mp="10uqg6s1npxr2" data-mpi={index}>
                    <motion.line x1={156} y1={fromY} x2={NODE_X} y2={CENTER_Y} stroke="#4338CA" strokeOpacity={0.25} strokeWidth={1.5} initial={{
                  pathLength: 0
                }} animate={{
                  pathLength: step >= 1 ? 1 : 0
                }} transition={{
                  duration: 0.7,
                  ease: EASE,
                  delay: index * 0.09
                }} data-id="element-2025" data-mp="10vrvj8pph6el" />
                    {step >= 2 && <motion.circle r={3} fill="#A855F7" animate={{
                  cx: [156, NODE_X],
                  cy: [fromY, CENTER_Y]
                }} transition={{
                  duration: 2.2,
                  repeat: Infinity,
                  ease: 'linear',
                  delay: index * 0.45
                }} data-id="element-2026" data-mp="39f8krdh0t79" />}
                  </g>;
            })}
            </svg>

            {STOREFRONTS.map((store, index) => <StorefrontCard key={store.name} name={store.name} kind={store.kind} y={store.y} active={active} delay={0.3 + index * 0.09} published={step >= 3} data-id="element-2027" data-mp="r51hompedx4x" data-mpi={index} />)}

            {/* Deployment channels */}
            <motion.div className="absolute left-[196px] flex flex-col gap-2" style={{
            top: 112
          }} initial={{
            opacity: 0
          }} animate={active ? {
            opacity: 1
          } : {
            opacity: 0
          }} transition={{
            duration: 0.4,
            delay: 0.7
          }} data-id="element-2028" data-mp="1ozuax6gw636p">
              {CHANNELS.map(channel => <span key={channel.label} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white border border-slate-200 text-[8.5px] text-slate-500" data-id="element-2029" data-mp="1a5fyjoru1s10">
                  <channel.icon className="w-2.5 h-2.5 text-primary-indigo" data-id="element-2030" data-mp="1f2zcenkmc2sf" />
                  {channel.label}
                </span>)}
            </motion.div>

            {/* Marketplace node */}
            <motion.div className="absolute w-[196px] rounded-2xl bg-dark-navy p-4" style={{
            left: NODE_X,
            top: CENTER_Y - 84,
            boxShadow: '0 30px 60px -24px rgba(11,18,32,0.5)'
          }} initial={{
            opacity: 0,
            scale: 0.96
          }} animate={step >= 2 ? {
            opacity: 1,
            scale: 1
          } : {
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.5,
            ease: EASE
          }} data-id="element-2031" data-mp="2qie7tmx6imlf">
              <DeckLogoPlate tone="dark" data-id="element-2032" data-mp="12l5azi70kqq0" />
              <p className="mt-3 font-display font-bold text-[15px] text-white leading-tight" data-id="element-2033" data-mp="12lyca3n7eo0r">
                Talab Marketplace
              </p>
              <p className="mt-1.5 text-[10.5px] leading-snug text-white/55" data-id="element-2034" data-mp="1chi34bmwuwin">
                Public inventory from every storefront, in one place customers can ask across.
              </p>
              <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2" data-id="element-2035" data-mp="1lj7wvo49m7jl">
                <span className="px-2 py-1 rounded-md bg-white/[0.08] text-[8.5px] text-white/70" data-id="element-2036" data-mp="3ibrj86c39y8m">
                  Phase 2
                </span>
                <span className="text-[8.5px] text-white/45" data-id="element-2037" data-mp="3nal10ayesbel">Opt-in, never required</span>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </SlideFrame>;
}